// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, X } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface CampaignFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface AdvertiserProspect {
  id: string;
  company_name: string;
  advertiser_tier: string;
  pipeline_stage: string;
}

const CampaignForm: React.FC<CampaignFormProps> = ({ open, onClose, onSuccess }) => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [advertisers, setAdvertisers] = useState<AdvertiserProspect[]>([]);
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  
  const [formData, setFormData] = useState({
    campaign_name: "",
    campaign_type: "",
    advertiser_id: "",
    total_budget: "",
    target_impressions: "",
    performance_notes: "",
    ad_placements: [] as string[],
  });

  const campaignTypes = [
    "Display Advertising",
    "Banner Ads",
    "Sponsored Content",
    "Newsletter Sponsorship",
    "Event Sponsorship",
    "Video Advertising",
    "Social Media Campaign",
    "Podcast Sponsorship"
  ];

  const adPlacements = [
    "Homepage Banner",
    "Sidebar Ads",
    "Article Header",
    "Newsletter",
    "Mobile Banner",
    "Video Pre-roll",
    "Social Media",
    "Email Signature",
    "Event Materials",
    "Podcast Spots"
  ];

  useEffect(() => {
    if (open) {
      fetchAdvertisers();
    }
  }, [open]);

  const fetchAdvertisers = async () => {
    try {
      const { data, error } = await supabase
        .from('advertiser_prospects')
        .select('id, company_name, advertiser_tier, pipeline_stage')
        .in('pipeline_stage', ['proposal', 'negotiation', 'agreement'])
        .order('company_name');

      if (error) throw error;
      setAdvertisers(data || []);
    } catch (error) {
      console.error('Error fetching advertisers:', error);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePlacementToggle = (placement: string) => {
    setFormData(prev => ({
      ...prev,
      ad_placements: prev.ad_placements.includes(placement)
        ? prev.ad_placements.filter(p => p !== placement)
        : [...prev.ad_placements, placement]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!startDate || !endDate) {
      toast({
        title: "Error",
        description: "Please select start and end dates for the campaign.",
        variant: "destructive",
      });
      return;
    }

    if (formData.ad_placements.length === 0) {
      toast({
        title: "Error", 
        description: "Please select at least one ad placement.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    
    try {
      const { error } = await supabase
        .from('advertising_campaigns')
        .insert({
          campaign_name: formData.campaign_name,
          campaign_type: formData.campaign_type,
          advertiser_id: formData.advertiser_id,
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0],
          total_budget: parseFloat(formData.total_budget),
          target_impressions: parseInt(formData.target_impressions),
          ad_placements: formData.ad_placements,
          performance_notes: formData.performance_notes,
          campaign_status: 'planned',
          actual_impressions: 0,
          click_through_rate: 0
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Campaign created successfully!",
      });
      
      onSuccess();
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast({
        title: "Error",
        description: "Failed to create campaign. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Campaign</DialogTitle>
          <DialogDescription>
            Set up a new advertising campaign for your client.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="campaign_name">Campaign Name*</Label>
              <Input
                id="campaign_name"
                value={formData.campaign_name}
                onChange={(e) => handleInputChange('campaign_name', e.target.value)}
                placeholder="e.g., Q1 Brand Awareness Campaign"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="advertiser">Advertiser*</Label>
              <Select 
                value={formData.advertiser_id} 
                onValueChange={(value) => handleInputChange('advertiser_id', value)}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select advertiser" />
                </SelectTrigger>
                <SelectContent>
                  {advertisers.map((advertiser) => (
                    <SelectItem key={advertiser.id} value={advertiser.id}>
                      {advertiser.company_name} ({advertiser.advertiser_tier})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="campaign_type">Campaign Type*</Label>
              <Select 
                value={formData.campaign_type} 
                onValueChange={(value) => handleInputChange('campaign_type', value)}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select campaign type" />
                </SelectTrigger>
                <SelectContent>
                  {campaignTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="total_budget">Total Budget*</Label>
              <Input
                id="total_budget"
                type="number"
                value={formData.total_budget}
                onChange={(e) => handleInputChange('total_budget', e.target.value)}
                placeholder="25000"
                required
              />
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date*</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "PPP") : "Select start date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>End Date*</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "PPP") : "Select end date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="target_impressions">Target Impressions</Label>
            <Input
              id="target_impressions"
              type="number"
              value={formData.target_impressions}
              onChange={(e) => handleInputChange('target_impressions', e.target.value)}
              placeholder="100000"
            />
          </div>

          {/* Ad Placements */}
          <div className="space-y-4">
            <Label>Ad Placements*</Label>
            <Card>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {adPlacements.map((placement) => (
                    <div key={placement} className="flex items-center space-x-2">
                      <Checkbox
                        id={placement}
                        checked={formData.ad_placements.includes(placement)}
                        onCheckedChange={() => handlePlacementToggle(placement)}
                      />
                      <Label htmlFor={placement} className="text-sm">
                        {placement}
                      </Label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-2">
            <Label htmlFor="performance_notes">Campaign Notes</Label>
            <Textarea
              id="performance_notes"
              value={formData.performance_notes}
              onChange={(e) => handleInputChange('performance_notes', e.target.value)}
              placeholder="Additional notes about campaign goals, target audience, etc."
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Creating..." : "Create Campaign"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignForm;